// ADR 23.9.26w §7 and §8 — the queue's React wiring, mounted once in `Shell`.
//
// It owns the triggers, and nothing else decides when a drain starts:
//
// - app start, and any change of signed-in owner — after the 30-day sweep
//   and a mirror read;
// - `onlineManager` going offline → online, subscribed directly. W2 feeds it
//   from `window` `online` **and** from its 30 s re-check after a transport
//   failure, so lie-fi recovery (no `online` event) drains too;
// - `lingara:session-refreshed`, for the `userId` it names;
// - `visibilitychange` to `visible` — the web's "app foregrounded" — which
//   also re-reads the mirror, so a tab another tab drained catches up.
//
// Every trigger goes through `requestDrain`'s guard (online, signed in).
//
// §8: ops are tagged with the session's `userId` and drained only for it.
// W2's purge (`onPurge`) resets **memory** — the mirror, the backoff, the pass
// in flight — and never deletes a stored op: they wait for their owner to sign
// back in on this browser, or expire after 30 days.

import { useEffect } from 'react'
import { onlineManager, useQueryClient } from '@tanstack/react-query'

import { useAuthSession } from '../../auth/session'
import { SESSION_REFRESHED_EVENT, type SessionIdentity } from '../../auth/tokenStore'
import { onPurge } from '../OfflineCache'
import { configureDrainer, requestDrain, resetDrainer } from './drainer'
import { refreshMirror, resetMirror, setOwner, sweepExpired } from './queue'

async function openFor(owner: string | null): Promise<void> {
  setOwner(owner)
  if (owner === null) return refreshMirror()
  await sweepExpired()
  await refreshMirror()
  await requestDrain()
}

function useWindowTriggers(): void {
  useEffect(() => {
    const onRefreshed = (event: Event) => {
      const { userId } = (event as CustomEvent<SessionIdentity>).detail
      setOwner(userId)
      void refreshMirror().then(requestDrain)
    }
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void refreshMirror().then(requestDrain)
    }
    window.addEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener(SESSION_REFRESHED_EVENT, onRefreshed)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
}

function useReconnectTrigger(): void {
  useEffect(() => {
    let wasOnline = onlineManager.isOnline()
    return onlineManager.subscribe((online) => {
      if (online && !wasOnline) void requestDrain()
      wasOnline = online
    })
  }, [])
}

export function useOfflineQueue(): void {
  const { status, userId } = useAuthSession()
  const client = useQueryClient()
  const owner = status === 'signedIn' ? userId : null

  useEffect(() => {
    configureDrainer({
      onChanged: () => {
        void client.invalidateQueries({ queryKey: ['flashcards'] })
        void client.invalidateQueries({ queryKey: ['flashcard-lists'] })
      },
    })
  }, [client])
  useEffect(
    () =>
      onPurge(() => {
        resetMirror()
        resetDrainer()
      }),
    [],
  )
  useEffect(() => {
    void openFor(owner)
  }, [owner])
  useWindowTriggers()
  useReconnectTrigger()
}
